// ============================================
// Report Message Modal Component
// Modal for reporting inappropriate chat messages
// ============================================

import React, { useState } from 'react';

const REPORT_CATEGORIES = [
  { value: 'harassment', label: 'Harassment or Bullying', description: 'Threatening, intimidating, or targeting behavior' },
  { value: 'spam', label: 'Spam', description: 'Unwanted promotional content or repetitive messages' },
  { value: 'inappropriate_content', label: 'Inappropriate Content', description: 'Offensive, explicit, or disturbing content' },
  { value: 'hate_speech', label: 'Hate Speech', description: 'Content that promotes hatred or discrimination' },
  { value: 'false_information', label: 'False Information', description: 'Deliberately misleading or false claims' },
  { value: 'privacy_violation', label: 'Privacy Violation', description: 'Sharing personal information without consent' },
  { value: 'other', label: 'Other', description: 'Other violation not listed above' }
];

/**
 * Modal for reporting chat messages for moderation review
 */
const ReportMessageModal = ({ isOpen, onClose, onSubmit, message, isSubmitting }) => {
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  if (!isOpen || !message) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!category) {
      setError('Please select a category');
      return;
    }

    if (reason.trim().length < 10) {
      setError('Please provide more details (at least 10 characters)');
      return;
    }

    if (reason.trim().length > 500) {
      setError('Description is too long (maximum 500 characters)');
      return;
    }

    onSubmit({
      content_type: 'message',
      content_id: message.id,
      category,
      reason: reason.trim()
    });
  };

  const handleClose = () => {
    setCategory('');
    setReason('');
    setError('');
    onClose();
  };

  const sender = message.sender || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">Report Message</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-red-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Message Preview */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-medium">
                {sender.display_name ? sender.display_name.charAt(0).toUpperCase() : '?'}
              </div>
              <span className="text-sm font-medium text-gray-700">{sender.display_name || 'Unknown'}</span>
            </div>
            <p className="text-sm text-gray-600 line-clamp-3">{message.content}</p>
          </div>

          {/* Category Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Why are you reporting this message? <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {REPORT_CATEGORIES.map((cat) => (
                <label
                  key={cat.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    category === cat.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={category === cat.value}
                    onChange={(e) => setCategory(e.target.value)}
                    className="mt-0.5 text-red-600 focus:ring-red-500"
                  />
                  <div className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">{cat.label}</span>
                    <span className="block text-xs text-gray-500">{cat.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Reason/Details */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional details <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please describe why this message is inappropriate..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={3}
              maxLength={500}
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">Minimum 10 characters</span>
              <span className={`text-xs ${reason.length > 450 ? 'text-red-500' : 'text-gray-500'}`}>
                {reason.length}/500
              </span>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Info note */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Reports are reviewed by our moderation team. False reports may result in action against your account.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !category || reason.trim().length < 10}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>Submit Report</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportMessageModal;
